import { Injectable, inject } from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { BehaviorSubject, interval } from 'rxjs';


@Injectable({
  providedIn: 'root'
})
export class ServiceWorkerService {

  private updates = inject(SwUpdate);
  private updateAvailable = new BehaviorSubject<boolean>(false);

  constructor() {
    if (this.updates.isEnabled) {
      this.listenForUpdates();
      this.schedulePeriodicUpdateCheck();
    }
  }

  checkForUpdates() {
    return this.updateAvailable.asObservable();
  }

  activateUpdate(): void {
    this.updates.activateUpdate().then(() => document.location.reload());
  }

  manualCheckForUpdate(): void {
    this.updates.checkForUpdate().then(() => {
      this.log('Manual update check complete.');
    });
  }

  skipUpdate(): void {
    this.updateAvailable.next(false);
  }

  confirmUpdate(): void {
    const userResponse = confirm('A new version is available. Do you want to update now?');
    if (userResponse) {
      this.activateUpdate();
    }
  }

  isServiceWorkerEnabled(): boolean {
    return this.updates.isEnabled;
  }

  private listenForUpdates(): void {
    this.updates.versionUpdates.subscribe((event: VersionEvent) => {
      switch (event.type) {
        case 'VERSION_DETECTED':
          this.log('New version detected, downloading...');
          break;
        case 'VERSION_READY':
          this.log('New version ready. Prompting user...');
          this.updateAvailable.next(true);
          break;
        case 'VERSION_INSTALLATION_FAILED':
          this.log(`Version installation failed: ${event.error}`, 'error');
          this.retryUpdateCheck(); // Retry update check if it fails
          break;
      }
    });

    this.updates.unrecoverable.subscribe(event => {
      this.log(`Unrecoverable error: ${event.reason}`, 'error');
      window.location.reload();
    });
  }

  private schedulePeriodicUpdateCheck(): void {
    interval(30 * 60 * 1000).subscribe(() => {
      this.log('Checking for updates...');
      this.updates.checkForUpdate();
    });
  }

  private retryUpdateCheck(retryAfterMs = 5 * 60 * 1000): void {
    setTimeout(() => {
      this.log('Retrying update check...');
      this.updates.checkForUpdate();
    }, retryAfterMs);
  }

  private log(message: string, level: 'info' | 'warn' | 'error' = 'info'): void {
    const logMessage = `[ServiceWorkerService] ${message}`;
    switch (level) {
      case 'info':
        console.log(logMessage);
        break;
      case 'warn':
        console.warn(logMessage);
        break;
      case 'error':
        console.error(logMessage);
        break;
    }
  }
}
